'use client'

import { motion } from 'framer-motion'
import { Container, Section, Spotlight, staggerContainer } from 'design-studio'
import { SectionHeader, SectionFooter } from '@/components/ui/section-header'
import { FeatureCard } from '@/components/ui/feature-card'

const CAPABILITIES = [
  {
    title: 'Prove & Refute',
    description:
      "Automatically prove theorems about your code — or synthesize concrete counterexamples when properties don't hold. Counterexamples are first-class executable values you can inspect and compute with.",
    accent: 'border-teal-500/30 hover:border-teal-400/50',
    dot: 'bg-teal-400',
  },
  {
    title: 'Region Decomposition',
    description:
      'Decompose the input space of any function into exhaustive, disjoint regions of behavior. A unique capability for test generation, coverage analysis and regulatory compliance.',
    accent: 'border-cyan-500/30 hover:border-cyan-400/50',
    dot: 'bg-cyan-400',
  },
  {
    title: 'Cloud-Native & AI-Ready',
    description:
      'A multicore, incremental architecture with an HTTP API, Python and OCaml client libraries, and seamless integration with AI agents and LLM-powered workflows.',
    accent: 'border-blue-500/30 hover:border-blue-400/50',
    dot: 'bg-blue-400',
  },
  {
    title: 'Interactive Proof Assistant',
    description:
      'A rich tactic language with 45+ tactics, a VS Code extension with goal state visualization, and incremental parallel proof checking for fast feedback.',
    accent: 'border-violet-500/30 hover:border-violet-400/50',
    dot: 'bg-violet-400',
  },
  {
    title: 'The Inductive Waterfall',
    description:
      'A powerful automated proof pipeline inspired by Boyer-Moore: simplification, unrolling, destructor elimination, fertilization, generalization and induction — all working together.',
    accent: 'border-indigo-500/30 hover:border-indigo-400/50',
    dot: 'bg-indigo-400',
  },
  {
    title: 'Battle-Tested',
    description:
      'Used in production for verifying financial systems, trading algorithms, regulatory compliance engines and safety-critical infrastructure.',
    accent: 'border-slate-500/30 hover:border-slate-400/50',
    dot: 'bg-slate-400',
  },
]

export function Capabilities() {
  return (
    <Section
      className="relative scroll-mt-32 overflow-hidden bg-slate-200/5 py-16 sm:pt-24 sm:pb-32 dark:bg-gray-900/5"
      id="capabilities"
    >
      <Container className="max-w-9xl px-4 sm:px-6 lg:px-8">
        <Spotlight
          color="from-indigo-600/10"
          className="-top-200 -right-200 z-0 size-512"
        />

        <SectionHeader
          pretitle="Capabilities"
          title="What ImandraX Can Do"
          description="Six core capabilities that set ImandraX apart from conventional testing, SMT solvers and static analysis tools."
          pretitleColor="text-cyan-500/80"
          className="relative z-10 mb-16"
        />

        <motion.div
          className="relative z-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.15 }}
          variants={staggerContainer}
        >
          {CAPABILITIES.map((cap, index) => (
            <FeatureCard
              key={index}
              title={cap.title}
              description={cap.description}
              dot={cap.dot}
              accent={cap.accent}
              delay={index * 0.08}
            />
          ))}
        </motion.div>

        <SectionFooter href="/docs/" className="mx-auto max-w-6xl">
          Explore the full documentation
        </SectionFooter>
      </Container>
    </Section>
  )
}
